import React, { useState, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://v2.schmalgsicht.de';

// Debug-Komponente zur Visualisierung der PKI-Ergebnisse
// Kann einfach ein-/ausgeschaltet werden über die ENABLED Konstante
const ENABLED = true;

export function PKIDebugPanel({ profileId, isVisible = true, hasPlayedTicTacToe = false, hasDrawn = false }) {
  const [profileData, setProfileData] = useState(null);
  const [activityData, setActivityData] = useState({ tictactoe: null, drawing: null });
  const [themesNextPeek, setThemesNextPeek] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Fetch profile data and activity results
  // Only poll when panel is expanded to reduce API load
  useEffect(() => {
    if (!profileId) return;
    // Don't poll if panel is collapsed - reduces API calls significantly
    if (!isExpanded) return;

    let interval = null;
    let isMounted = true;

    const fetchData = async () => {
      if (!isMounted) return;

      setLoading(true);
      setError(null);
      try {
        // Fetch main profile data
        const profileResponse = await fetch(`${API_BASE_URL}/api/profile/${profileId}`);
        if (profileResponse.ok) {
          const data = await profileResponse.json();
          if (isMounted) {
            setProfileData(data);
          }
        } else {
          if (isMounted) setError('Profil nicht gefunden');
        }

        // Fetch TicTacToe results only if user has played
        if (hasPlayedTicTacToe) {
          try {
            const tttResponse = await fetch(`${API_BASE_URL}/api/profile/${profileId}/tictactoe`);
            if (tttResponse.ok) {
              const tttData = await tttResponse.json();
              // Data is nested in tictactoe_result
              if (isMounted) setActivityData(prev => ({ ...prev, tictactoe: tttData.tictactoe_result }));
            }
          } catch (e) {
            // TicTacToe data not available yet
          }
        }

        // Fetch Drawing analysis only if user has drawn
        if (hasDrawn) {
          try {
            const drawResponse = await fetch(`${API_BASE_URL}/api/profile/${profileId}/drawing`);
            if (drawResponse.ok) {
              const drawData = await drawResponse.json();
              // Data is nested in drawing_analysis
              if (isMounted) setActivityData(prev => ({ ...prev, drawing: drawData.drawing_analysis }));
            }
          } catch (e) {
            // Drawing data not available yet
          }
        }

        // Fetch themes/next/peek (shows what /themes/next would return, including activity status)
        try {
          const themesResponse = await fetch(`${API_BASE_URL}/api/profile/${profileId}/themes/next/peek`);
          if (themesResponse.ok) {
            const themesData = await themesResponse.json();
            if (isMounted) setThemesNextPeek(themesData);
          }
        } catch (e) {
          // Themes peek not available
        }

      } catch (err) {
        if (isMounted) setError('Server nicht erreichbar');
      }
      if (isMounted) setLoading(false);
    };

    fetchData();
    // Poll every 15 seconds (was 3s - caused 429 rate limit errors)
    interval = setInterval(fetchData, 15000);

    return () => {
      isMounted = false;
      if (interval) clearInterval(interval);
    };
  }, [profileId, hasPlayedTicTacToe, hasDrawn, isExpanded]);

  // Nicht rendern wenn deaktiviert oder keine profileId
  if (!ENABLED || !profileId || !isVisible) return null;

  const inputValues = profileData?.data?.input_values;
  const listenerProfile = profileData?.data?.listener_profile;
  const themes = profileData?.data?.theme_recommendations;
  const experimental = profileData?.data?.experimental_topics;
  const programBlock = profileData?.data?.program_block;

  // Farbmapping (Freundebuch - deutsche Namen)
  const colorMap = {
    rot: '#ff6b6b',
    gelb: '#feca57',
    grün: '#1dd1a1',
    blau: '#48dbfb',
    orange: '#ff9f43',
    pink: '#ff9ff3',
    lila: '#a55eea',
    türkis: '#00d2d3',
    weiß: '#f8f8f8'
  };

  // Mood-Emoji Mapping (Freundebuch)
  const moodEmoji = {
    happy: '😊',
    okay: '😐',
    sad: '😢',
    angry: '😠'
  };

  // Activity-Emoji Mapping (Freundebuch)
  const activityEmoji = {
    working: '💻',
    chilling: '🛋️',
    traveling: '✈️',
    cooking: '🍳',
    sports: '🏃',
    eating: '🍕',
    tv: '📺',
    learning: '📚'
  };

  // Living Emoji Mapping
  const livingEmoji = {
    city: '🏙️',
    country: '🌳'
  };

  // TicTacToe result mapping
  const tttResultMap = {
    player_win: { text: 'Du gewinnst!', color: 'text-green-600', bg: 'bg-green-100' },
    chris_win: { text: 'Chris gewinnt!', color: 'text-pink-600', bg: 'bg-pink-100' },
    draw: { text: 'Unentschieden', color: 'text-yellow-600', bg: 'bg-yellow-100' }
  };

  // Helper: Finde das nächste verfügbare Theme (ohne es als used zu markieren)
  const getNextAvailableTheme = () => {
    const CORE_CATEGORIES = ['trending_topics', 'artist_news', 'events', 'fun_facts', 'job_facts'];
    const EXP_CATEGORIES = ['genre_twist', 'nischen_trends', 'kultur_kontrast', 'random_discovery', 'cross_demographic'];

    // Erst Core Themes prüfen
    if (themes?.themes && typeof themes.themes === 'object') {
      for (const category of CORE_CATEGORIES) {
        const items = themes.themes[category];
        if (Array.isArray(items)) {
          const unused = items.find(item => item && !item.used);
          if (unused) {
            return { text: unused.text, category, type: 'core' };
          }
        }
      }
    }

    // Dann Experimental Themes
    if (experimental?.topics && typeof experimental.topics === 'object') {
      for (const category of EXP_CATEGORIES) {
        const items = experimental.topics[category];
        if (Array.isArray(items)) {
          const unused = items.find(item => item && !item.used);
          if (unused) {
            return { text: unused.text, category, type: 'exp' };
          }
        }
      }
    }

    return null;
  };

  return (
    <div
      className="hidden sm:block fixed bottom-4 left-32 z-50 pointer-events-auto"
      style={{ maxWidth: isExpanded ? '400px' : '60px' }}
    >
      {/* Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="absolute top-0 right-0 w-8 h-8 bg-gray-800 text-white rounded-full flex items-center justify-center text-xs font-bold hover:bg-gray-700 transition-colors z-10"
        style={{ transform: 'translate(50%, -50%)' }}
      >
        {isExpanded ? '×' : '📊'}
      </button>

      {/* Collapsed View - Mini Status */}
      {!isExpanded && (
        <div className="bg-white rounded-xl shadow-lg p-3 flex items-center gap-2">
          <div
            className={`w-3 h-3 rounded-full ${profileData?.status === 'complete' ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`}
          />
          <span className="text-xs font-medium text-gray-600">PKI</span>
        </div>
      )}

      {/* Expanded View */}
      {isExpanded && (
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-pink-500 px-4 py-3">
            <h3 className="text-white font-bold text-sm">PKI Debug Panel</h3>
          </div>

          {/* Profile ID - prominent display */}
          <div className="bg-gray-900 px-4 py-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">Profile ID</span>
              <code className="text-sm font-mono text-green-400 select-all">{profileId}</code>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
            {loading && !profileData && (
              <div className="text-center text-gray-500 py-4">
                <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
                Lade Daten...
              </div>
            )}

            {error && (
              <div className="text-center text-red-500 py-4 text-sm">
                {error}
              </div>
            )}

            {/* Input Values - Freundebuch */}
            {inputValues && (
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Freundebuch</h4>

                {/* Name */}
                {inputValues.name && (
                  <div className="bg-gradient-to-r from-blue-100 to-pink-100 rounded-lg p-2">
                    <div className="text-xs text-gray-500">Name</div>
                    <div className="text-sm font-bold">{inputValues.name}</div>
                  </div>
                )}

                {/* Mood, Activity, Color */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-gray-100 rounded-lg p-2 text-center">
                    <div className="text-xl">{moodEmoji[inputValues.mood] || '❓'}</div>
                    <div className="text-xs text-gray-600 mt-1">{inputValues.mood}</div>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-2 text-center">
                    <div className="text-xl">{activityEmoji[inputValues.activity] || '✏️'}</div>
                    <div className="text-xs text-gray-600 mt-1 truncate" title={inputValues.activity}>{inputValues.activity}</div>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-2 text-center">
                    <div
                      className="w-6 h-6 rounded-full mx-auto border-2 border-white shadow"
                      style={{ backgroundColor: colorMap[inputValues.color] || '#888' }}
                    />
                    <div className="text-xs text-gray-600 mt-1">{inputValues.color}</div>
                  </div>
                </div>

                {/* Age, Living, Job */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-gray-100 rounded-lg p-2 text-center">
                    <div className="text-xs text-gray-500">Alter</div>
                    <div className="text-sm font-bold">{inputValues.age || '?'}</div>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-2 text-center">
                    <div className="text-xl">{livingEmoji[inputValues.living] || '❓'}</div>
                    <div className="text-xs text-gray-600 mt-1">{inputValues.living === 'city' ? 'Stadt' : 'Land'}</div>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-2 text-center">
                    <div className="text-xs text-gray-500">Beruf</div>
                    <div className="text-xs font-bold truncate">{inputValues.job || '?'}</div>
                  </div>
                </div>

                {/* City */}
                {inputValues.city && (
                  <div className="bg-gray-100 rounded-lg p-2">
                    <div className="text-xs text-gray-500">📍 Ort</div>
                    <div className="text-sm font-bold">{inputValues.city}</div>
                  </div>
                )}

                {/* Interests */}
                {inputValues.interests && inputValues.interests.length > 0 && (
                  <div className="bg-gray-100 rounded-lg p-2">
                    <div className="text-xs text-gray-500 mb-1">🎯 Interessen</div>
                    <div className="flex flex-wrap gap-1">
                      {inputValues.interests.map((interest, i) => (
                        <span key={i} className="text-xs bg-white px-2 py-0.5 rounded-full border border-gray-200">
                          {interest}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Genres */}
                {inputValues.genres && inputValues.genres.length > 0 && (
                  <div className="bg-gray-100 rounded-lg p-2">
                    <div className="text-xs text-gray-500 mb-1">🎵 Musik-Genres</div>
                    <div className="flex flex-wrap gap-1">
                      {inputValues.genres.map((genre, i) => (
                        <span key={i} className="text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full border border-pink-200">
                          {genre}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Day Plan */}
                {inputValues.dayPlan && (
                  <div className="bg-gray-100 rounded-lg p-2">
                    <div className="text-xs text-gray-500">📅 Tagesplan</div>
                    <div className="text-xs text-gray-700 mt-1">{inputValues.dayPlan}</div>
                  </div>
                )}
              </div>
            )}

            {/* Themes/Next Preview - was bei /themes/next käme */}
            {themesNextPeek && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {themesNextPeek.has_activity_intro ? '🎯 Activity + Theme (Preview)' : '📋 Next Theme (Preview)'}
                  </h4>
                  {themesNextPeek.has_activity_intro ? (
                    <span className="text-[10px] bg-green-500 text-white px-2 py-0.5 rounded-full animate-pulse">
                      ⏳ Activity wartet
                    </span>
                  ) : (
                    <span className="text-[10px] bg-gray-400 text-white px-2 py-0.5 rounded-full">
                      Kein Activity-Intro
                    </span>
                  )}
                </div>
                <div className={`rounded-lg p-3 border-2 ${themesNextPeek.has_activity_intro ? 'bg-gradient-to-r from-purple-100 to-pink-100 border-purple-300' : 'bg-gray-100 border-gray-300'}`}>
                  <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {themesNextPeek.theme || 'Keine Themes verfügbar'}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {themesNextPeek.activity_type === 'tictactoe' && (
                      <span className="text-[10px] bg-blue-200 text-blue-700 px-2 py-0.5 rounded-full">🎮 TicTacToe</span>
                    )}
                    {themesNextPeek.activity_type === 'drawing' && (
                      <span className="text-[10px] bg-orange-200 text-orange-700 px-2 py-0.5 rounded-full">🎨 Drawing</span>
                    )}
                    {themesNextPeek.activity_type === 'both' && (
                      <>
                        <span className="text-[10px] bg-blue-200 text-blue-700 px-2 py-0.5 rounded-full">🎮 TicTacToe</span>
                        <span className="text-[10px] bg-orange-200 text-orange-700 px-2 py-0.5 rounded-full">🎨 Drawing</span>
                      </>
                    )}
                    {themesNextPeek.type === 'core' && (
                      <span className="text-[10px] bg-green-200 text-green-700 px-2 py-0.5 rounded-full">Core: {themesNextPeek.category}</span>
                    )}
                    {themesNextPeek.type === 'exp' && (
                      <span className="text-[10px] bg-yellow-200 text-yellow-700 px-2 py-0.5 rounded-full">Exp: {themesNextPeek.category}</span>
                    )}
                    {themesNextPeek.type === 'activity' && (
                      <span className="text-[10px] bg-purple-200 text-purple-700 px-2 py-0.5 rounded-full">Nur Activity (kein Theme)</span>
                    )}
                    <span className="text-[10px] bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                      #{themesNextPeek.request_number} | Exp-Intervall: {themesNextPeek.exp_interval}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Live Activities - TicTacToe & Drawing */}
            {(activityData.tictactoe || activityData.drawing) && (
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Live-Aktivitäten</h4>

                {/* TicTacToe Result */}
                {activityData.tictactoe && (
                  <div className={`rounded-lg p-3 ${tttResultMap[activityData.tictactoe.result]?.bg || 'bg-gray-100'}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🎮</span>
                      <div className="flex-1">
                        <div className="text-xs text-gray-500">Tic Tac Toe</div>
                        <div className={`font-bold ${tttResultMap[activityData.tictactoe.result]?.color || 'text-gray-700'}`}>
                          {tttResultMap[activityData.tictactoe.result]?.text || activityData.tictactoe.result || 'Gespielt'}
                        </div>
                      </div>
                      {/* Mini Board Preview */}
                      {activityData.tictactoe.board && (
                        <div className="grid grid-cols-3 gap-0.5">
                          {activityData.tictactoe.board.map((cell, i) => (
                            <div
                              key={i}
                              className={`w-4 h-4 flex items-center justify-center text-xs font-bold rounded ${
                                cell === 'X' ? 'bg-blue-500 text-white' :
                                cell === 'O' ? 'bg-pink-500 text-white' :
                                'bg-white/50'
                              }`}
                            >
                              {cell || ''}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Drawing Analysis */}
                {activityData.drawing && (
                  <div className="bg-orange-50 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🎨</span>
                      <div className="flex-1">
                        <div className="text-xs text-gray-500">Zeichnung</div>
                        <div className="font-bold text-orange-700">
                          {activityData.drawing.status === 'processing'
                            ? 'Wird analysiert...'
                            : activityData.drawing.analysis?.interpretation_data?.style || 'Analysiert'}
                        </div>
                      </div>
                      {activityData.drawing.status === 'processing' && (
                        <div className="animate-spin w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full" />
                      )}
                    </div>
                    {/* Drawing status/result */}
                    {activityData.drawing.status === 'processing' ? (
                      <p className="text-xs text-gray-500 mt-2">
                        GPT-4 analysiert deine Zeichnung... ({activityData.drawing.pixel_count} Pixel)
                      </p>
                    ) : activityData.drawing.interpretation_prompt ? (
                      <p className="text-xs text-gray-600 mt-2 leading-relaxed">
                        {activityData.drawing.interpretation_prompt}
                      </p>
                    ) : activityData.drawing.analysis?.interpretation_data && (
                      <div className="text-xs text-gray-600 mt-2 space-y-1">
                        <div>Stil: {activityData.drawing.analysis.interpretation_data.style}</div>
                        <div>Komplexität: {activityData.drawing.analysis.interpretation_data.complexity}</div>
                        <div>Farbvielfalt: {activityData.drawing.analysis.interpretation_data.color_variety}</div>
                        <div>Fokus: {activityData.drawing.analysis.interpretation_data.spatial_focus}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Listener Profile */}
            {listenerProfile && (
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Hörer-Profil</h4>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-gray-700 leading-relaxed">
                    {listenerProfile.profile}
                  </p>
                </div>
              </div>
            )}

            {/* Theme Recommendations */}
            {themes && (
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                  Core Themes
                  {themes.themes_type === 'json' && (
                    <span className="text-[10px] bg-purple-200 text-purple-700 px-1.5 py-0.5 rounded">JSON</span>
                  )}
                  {themes.themes_type === 'text' && (
                    <span className="text-[10px] bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">TEXT</span>
                  )}
                </h4>
                {/* Check if themes.themes is JSON object or legacy text */}
                {typeof themes.themes === 'object' && themes.themes !== null ? (
                  <div className="space-y-2">
                    {(() => {
                      // Helper function to normalize theme data (handles array, object, and string formats)
                      const normalizeThemes = (data) => {
                        if (!data) return [];
                        if (Array.isArray(data)) return data;
                        if (typeof data === 'string') return [{ text: data, used: false }];
                        if (typeof data === 'object') {
                          return Object.entries(data).map(([key, value]) => ({
                            text: typeof value === 'string' ? `${key}: ${value}` : key,
                            used: false
                          }));
                        }
                        return [];
                      };

                      const renderCategory = (data, label, emoji, bgColor, textColor) => {
                        const items = normalizeThemes(data);
                        if (items.length === 0) return null;
                        return (
                          <div className={`${bgColor} rounded-lg p-2`}>
                            <div className={`text-[10px] font-bold ${textColor} uppercase mb-1`}>{emoji} {label}</div>
                            {items.map((item, i) => {
                              const text = typeof item === 'object' ? item.text : item;
                              const used = typeof item === 'object' ? item.used : false;
                              return (
                                <p key={i} className={`text-xs leading-relaxed mb-1 ${used ? 'text-red-500 line-through opacity-60' : 'text-green-700'}`}>
                                  {used ? '✗' : '✓'} {text}
                                </p>
                              );
                            })}
                          </div>
                        );
                      };

                      return (
                        <>
                          {renderCategory(themes.themes.trending_topics, 'Trending Topics', '📈', 'bg-purple-50', 'text-purple-600')}
                          {renderCategory(themes.themes.artist_news, 'Artist News', '🎤', 'bg-indigo-50', 'text-indigo-600')}
                          {renderCategory(themes.themes.events, 'Events', '📅', 'bg-blue-50', 'text-blue-600')}
                          {renderCategory(themes.themes.fun_facts, 'Fun Facts', '💡', 'bg-amber-50', 'text-amber-600')}
                          {renderCategory(themes.themes.weather, 'Wetter', '🌤️', 'bg-cyan-50', 'text-cyan-600')}
                          {renderCategory(themes.themes.job_facts, 'Job Facts', '💼', 'bg-emerald-50', 'text-emerald-600')}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {themes.themes}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Experimental Topics */}
            {experimental && (
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                  Experimental
                  {experimental.topics_type === 'json' && (
                    <span className="text-[10px] bg-pink-200 text-pink-700 px-1.5 py-0.5 rounded">JSON</span>
                  )}
                </h4>
                {/* Check if experimental.topics is JSON object or legacy text */}
                {typeof experimental.topics === 'object' && experimental.topics !== null ? (
                  <div className="space-y-2">
                    {(() => {
                      // Helper function to safely normalize and render topics
                      const normalizeTopics = (data) => {
                        if (!data) return [];
                        if (Array.isArray(data)) return data;
                        if (typeof data === 'object') {
                          return Object.entries(data).map(([key, value]) => ({
                            text: `${key}: ${value}`,
                            used: false
                          }));
                        }
                        return [];
                      };

                      const renderExpCategory = (data, label, emoji, bgColor, textColor) => {
                        const items = normalizeTopics(data);
                        if (items.length === 0) return null;
                        return (
                          <div className={`${bgColor} rounded-lg p-2`}>
                            <div className={`text-[10px] font-bold ${textColor} uppercase mb-1`}>{emoji} {label}</div>
                            {items.map((item, i) => {
                              const text = typeof item === 'object' ? item.text : item;
                              const used = typeof item === 'object' ? item.used : false;
                              return (
                                <p key={i} className={`text-xs leading-relaxed mb-1 ${used ? 'text-red-500 line-through opacity-60' : 'text-green-700'}`}>
                                  {used ? '✗' : '✓'} {text}
                                </p>
                              );
                            })}
                          </div>
                        );
                      };

                      return (
                        <>
                          {renderExpCategory(experimental.topics.genre_twist, 'Genre Twist', '🎸', 'bg-pink-50', 'text-pink-600')}
                          {renderExpCategory(experimental.topics.nischen_trends, 'Nischen Trends', '🔮', 'bg-fuchsia-50', 'text-fuchsia-600')}
                          {renderExpCategory(experimental.topics.kultur_kontrast, 'Kultur Kontrast', '🎭', 'bg-rose-50', 'text-rose-600')}
                          {renderExpCategory(experimental.topics.random_discovery, 'Random Discovery', '🎲', 'bg-orange-50', 'text-orange-600')}
                          {renderExpCategory(experimental.topics.cross_demographic, 'Cross Demographic', '👥', 'bg-violet-50', 'text-violet-600')}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="bg-pink-50 rounded-lg p-3">
                    <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {experimental.topics}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Program Block */}
            {programBlock && (
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Programm-Block</h4>
                <div className="bg-green-50 rounded-lg p-3 space-y-2">
                  {/* Block String */}
                  <div className="font-mono text-xs bg-white rounded p-2 overflow-x-auto">
                    {programBlock.block?.block_string || 'N/A'}
                  </div>

                  {/* Statistics */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-lg font-bold text-blue-500">
                        {programBlock.block?.statistics?.mus_count || 0}
                      </div>
                      <div className="text-xs text-gray-500">Musik</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-purple-500">
                        {programBlock.block?.statistics?.core_count || 0}
                      </div>
                      <div className="text-xs text-gray-500">Core</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-pink-500">
                        {programBlock.block?.statistics?.exp_count || 0}
                      </div>
                      <div className="text-xs text-gray-500">Exp</div>
                    </div>
                  </div>

                  {/* Experimental Ratio */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">🧪 Exp Ratio:</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-purple-400 to-pink-500 h-3 rounded-full transition-all"
                        style={{ width: `${(programBlock.experimental_ratio || programBlock.energy_calculation?.experimental_ratio || 0) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-bold text-pink-600">
                      {((programBlock.experimental_ratio || programBlock.energy_calculation?.experimental_ratio || 0) * 100).toFixed(0)}%
                    </span>
                  </div>

                  {/* Scores Detail */}
                  {programBlock.scores && (
                    <div className="grid grid-cols-4 gap-1 text-center text-xs">
                      <div className="bg-white rounded p-1">
                        <div className="font-bold text-yellow-600">{programBlock.scores.mood_score}</div>
                        <div className="text-gray-400">Mood</div>
                      </div>
                      <div className="bg-white rounded p-1">
                        <div className="font-bold text-blue-600">{programBlock.scores.urgency_score}</div>
                        <div className="text-gray-400">Zeit</div>
                      </div>
                      <div className="bg-white rounded p-1">
                        <div className="font-bold text-green-600">{programBlock.scores.interests_score}</div>
                        <div className="text-gray-400">Inter.</div>
                      </div>
                      <div className="bg-white rounded p-1">
                        <div className="font-bold text-pink-600">{programBlock.scores.genres_score}</div>
                        <div className="text-gray-400">Genres</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
}
